import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium",
    "transition-all duration-150 ease-out",
    "focus-visible:outline-none",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "active:scale-[0.97]",
  ].join(" "),
  {
    variants: {
      variant: {
        default: [
          "text-white",
          "border border-brand/20 bg-brand/10 hover:border-brand/40 hover:bg-brand/20",
          "shadow-brand/60 hover:shadow-brand",
          "focus-visible:ring-2 focus-visible:ring-brand/60",
        ].join(" "),
        destructive: [
          "border border-destructive/40 bg-destructive/10 text-destructive",
          "hover:border-destructive/50 hover:bg-destructive/20 active:bg-destructive/20",
          "focus-visible:ring-2 focus-visible:ring-destructive/45",
        ].join(" "),
        outline: [
          "border border-border/70 bg-card/70 hover:bg-brand/5",
          "hover:border-brand/50 hover:shadow-brand",
          "text-foreground",
        ].join(" "),
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted text-foreground/85 hover:text-foreground",
        link: "text-brand underline-offset-4 hover:underline",
        soft: [
          "border border-brand/20 bg-brand/10 text-brand hover:border-brand/40 hover:bg-brand/20",
        ].join(" "),
      },
      size: {
        default: "h-11 px-4 py-2",
        sm: "h-9 px-3 text-xs",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

const Button = React.forwardRef(({ className, variant, size, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";
  return (
    <Comp
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
